import { jsToFlutter } from "./js-to-flutter";

const MINI_PROGRAM_SDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.3.2.js";

export function isMiniProgramContainer() {
  return window.__wxjs_environment === "miniprogram";
}

function isHXAppContainer() {
  return /mgyk-ios-app|mgyk-android-app/i.test(
    navigator.userAgent.toLocaleLowerCase()
  );
}

function normalizeVideoParams(params = {}) {
  const courseId = params.courseId || params.course_id;
  const unitId = params.unitId || params.unit_id;
  const lessonId = params.lessonId || params.lesson_id || params.id;
  if (!courseId || !unitId || !lessonId) return null;
  return {
    courseId,
    unitId,
    lessonId,
  };
}

function normalizeCourseDetailParams(params = {}) {
  const courseId = params.courseId || params.course_id || params.id;
  if (!courseId) return null;
  return { courseId };
}

function loadMiniProgramSdk(callback, onError = () => {}) {
  if (window.wx?.miniProgram) {
    callback();
    return;
  }

  const existingScript = document.querySelector(
    `script[src="${MINI_PROGRAM_SDK_URL}"]`
  );
  if (existingScript) {
    existingScript.addEventListener("load", callback, { once: true });
    existingScript.addEventListener(
      "error",
      () => {
        existingScript.remove();
        onError();
      },
      { once: true }
    );
    return;
  }

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = MINI_PROGRAM_SDK_URL;
  script.onload = callback;
  script.onerror = () => {
    script.remove();
    onError();
  };
  document.head.appendChild(script);
}

function openMiniProgramVideoPlayer(params = {}) {
  const normalized = normalizeVideoParams(params);
  if (!normalized) return false;

  const { courseId, unitId, lessonId } = normalized;
  const url = `/learning/video-play/index?courseId=${courseId}&unitId=${unitId}&lessonId=${lessonId}`;
  loadMiniProgramSdk(() => {
    window.wx?.miniProgram?.navigateTo({ url });
  });
  return true;
}

function openAppVideoPlayer(params = {}) {
  const normalized = normalizeVideoParams(params);
  if (!normalized) return false;

  const { courseId, unitId, lessonId } = normalized;
  jsToFlutter("videoPlayer", {
    course_id: courseId,
    unit_id: unitId,
    lesson_id: lessonId,
  });
  return true;
}

function openMiniProgramCourseDetail(params = {}) {
  const normalized = normalizeCourseDetailParams(params);
  if (!normalized) return false;

  loadMiniProgramSdk(() => {
    window.wx?.miniProgram?.redirectTo({
      url: `/learning/course-detail/index?courseId=${normalized.courseId}`,
    });
  });
  return true;
}

function openAppCourseDetail(params = {}) {
  const normalized = normalizeCourseDetailParams(params);
  if (!normalized) return false;

  jsToFlutter(
    "openPage",
    { courseId: normalized.courseId },
    { name: "myCourseDetail" }
  );
  return true;
}

export const nativeBridge = {
  openProductLogin({ courseId, purchase = false, activityId = null } = {}) {
    if (!isMiniProgramContainer()) return Promise.resolve(false);
    if (!/^\d+$/.test(String(courseId || ""))) return Promise.reject(new Error("商品参数无效"));
    const intent = purchase ? `&purchase=1&activityId=${encodeURIComponent(activityId == null ? "" : activityId)}` : "";
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) reject(error); else resolve(true);
      };
      const timer = setTimeout(() => finish(new Error("打开登录页面超时，请重试")), 10000);
      const fail = () => finish(new Error("无法打开小程序登录，请重试"));
      loadMiniProgramSdk(() => {
        if (settled) return;
        if (!window.wx?.miniProgram?.redirectTo) return fail();
        try {
          window.wx.miniProgram.redirectTo({
            url: `/course/product-detail/index?id=${encodeURIComponent(courseId)}&login=1${intent}`,
            fail,
          });
          finish();
        } catch (_) { fail(); }
      }, fail);
    });
  },
  // A web-view bridge may not invoke navigation callbacks, so resolve after dispatch.
  openPayment({ order_no, token } = {}) {
    if (!isMiniProgramContainer()) return Promise.resolve(false);
    if (!order_no || !token)
      return Promise.reject(new Error("支付参数不完整，请刷新后重试"));

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) reject(error);
        else resolve(true);
      };
      const timer = setTimeout(
        () => finish(new Error("打开支付页面超时，请重试")),
        10000
      );
      const fail = () => finish(new Error("无法打开微信支付页面，请重试"));
      loadMiniProgramSdk(() => {
        if (settled) return;
        if (!window.wx?.miniProgram?.navigateTo) return fail();
        try {
          window.wx.miniProgram.navigateTo({
            url: `/pages/pay/index?order_no=${encodeURIComponent(
              order_no
            )}&token=${encodeURIComponent(token)}&source=miniProduct`,
            success: () => finish(),
            fail,
          });
          finish();
        } catch (_) {
          fail();
        }
      }, fail);
    });
  },

  openVideoPlayer(params = {}) {
    if (isMiniProgramContainer()) {
      return openMiniProgramVideoPlayer(params);
    }
    if (isHXAppContainer()) {
      return openAppVideoPlayer(params);
    }
    return false;
  },

  openCourseDetail(params = {}) {
    if (isMiniProgramContainer()) {
      return openMiniProgramCourseDetail(params);
    }
    if (isHXAppContainer()) {
      return openAppCourseDetail(params);
    }
    return false;
  },
};
